import { getReportAuthorEmail } from "./reportAuthor";
import {
	collectAuthorEmails,
	getReportAuthorName,
	getReportPlaceParts,
	StreamPlaceReport,
	StreamPlaces,
} from "./reportStream";
import { collectReportExportEmails } from "./reportStreamExport";

// The fields of an OFI the OFI stream reads.
export type StreamOfi = StreamPlaceReport & {
	report_number?: unknown;
	title?: unknown;
	description?: unknown;
	is_anonymous?: unknown;
	reviewer?: unknown;
	operator?: string;
	primary_author?: string | null;
	author?: string | null;
};

// Any stored value that says so, as the OFI page reads it: an OFI filed
// anonymously names nobody.
export const isAnonymousOfi = (report: StreamOfi) =>
	Boolean(report.is_anonymous);

// The authors to look up by name: none of an anonymous OFI.
export const collectOfiAuthorEmails = (rows: readonly StreamOfi[]) =>
	collectAuthorEmails(rows.filter((row) => !isAnonymousOfi(row)));

// The author as the stream shows them.
export const getOfiAuthorName = (
	report: StreamOfi,
	authorNames: Record<string, string>
) =>
	isAnonymousOfi(report)
		? "Anonymous"
		: getReportAuthorName(report, authorNames);

// What the stream's search looks through for an OFI. An anonymous OFI's
// author isn't searched, so a name never finds it.
export function getOfiSearchTexts(
	report: StreamOfi,
	places: StreamPlaces,
	authorNames: Record<string, string>,
	getTenantName: (tenantKey: string) => string | undefined
) {
	const author = isAnonymousOfi(report)
		? []
		: [getReportAuthorEmail(report), getReportAuthorName(report, authorNames)];
	return [
		report.report_number,
		report.title,
		report.description,
		...getReportPlaceParts(report, places),
		...author,
		report.operator ? getTenantName(report.operator) : undefined,
	];
}

// Everyone an OFI's PDF names, for the names lookup: its reviewer and its
// actions' people, and its author unless it is anonymous.
export const collectOfiExportEmails = (
	report: StreamOfi & Record<string, any>
) =>
	collectReportExportEmails(
		isAnonymousOfi(report)
			? { ...report, primary_author: null, author: null }
			: report,
		"follow_up_actions"
	);

// The OFI as its PDF reads it: the approval record names an anonymous OFI's
// author "Anonymous", as the OFI page's export does.
export const getOfiExportData = <T extends StreamOfi>(report: T) =>
	isAnonymousOfi(report) ? { ...report, author_name: "Anonymous" } : report;
